import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface IgProject {
  id: string;
  slug: string;
  ig_username: string;
  ig_business_account_id: string;
  fb_page_id: string | null;
  brand_context: Record<string, unknown>;
  hashtag_config: Record<string, unknown>;
  format_mix: Record<string, number>;
  publish_to: string[];
  timezone: string;
  posts_per_week: number;
  is_active: boolean;
  rotation_cycle: string[];
  rotation_cursor: number;
  content_variants: string[];
  visual_formats: string[];
  created_at: string;
  updated_at: string;
}

export interface IgStats {
  queue_total: number;
  queue_ready: number;
  queue_failed: number;
  published_total: number;
  last_runs: { step: string; status: string; finished_at: string; duration_ms: number }[];
}

export function useIgProject(projectSlug: string) {
  const [project, setProject] = useState<IgProject | null>(null);
  const [stats, setStats] = useState<IgStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const [projRes, statsRes] = await Promise.all([
      supabase.functions.invoke("ig-admin-api", { body: { action: "get_project", project: projectSlug } }),
      supabase.functions.invoke("ig-admin-api", { body: { action: "get_stats", project: projectSlug } }),
    ]);
    if (projRes.data?.project) setProject(projRes.data.project);
    if (statsRes.data?.stats) setStats(statsRes.data.stats);
    setLoading(false);
  }, [projectSlug]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateProject = useCallback(async (updates: Partial<Pick<IgProject, "posts_per_week" | "format_mix" | "hashtag_config" | "brand_context" | "is_active" | "content_variants" | "visual_formats">>) => {
    const { error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "update_project", project: projectSlug, ...updates },
    });
    if (!error) await fetch();
    return !error;
  }, [fetch, projectSlug]);

  return { project, stats, loading, refetch: fetch, updateProject };
}
